import React, { useMemo } from "react";
import { StyleSheet } from "react-native";

import { palette } from "../styles";
import Block from "./Block";
import { Image, type ImageProps } from "./Image";
import SvgIcon from "./SvgIcon";

type Props = {
  size?: number;
  confirmed?: boolean;
  style?: ImageProps["style"];
  placeholder?: Maybe<string>;
} & ({ uri?: Maybe<string>; source?: undefined } | { source: NotWorth; uri?: undefined });

const Avatar = ({ uri, size = 50, style, placeholder, source, confirmed }: Props) => {
  const imgSource = useMemo(() => {
    if (uri && typeof uri === "string" && (uri.startsWith("data") || uri.startsWith("https"))) {
      return { uri };
    }
    return source || placeholder;
  }, [placeholder, source, uri]);

  const roundedSizeStyle = useMemo(
    () => ({
      height: size,
      width: size,
      borderRadius: size / 2,
      overflow: "auto" as const,
    }),
    [size],
  ) as ToDo as Props["style"];
  // "overflow: auto" not recognized in React Native, but this component is web only

  return (
    <>
      <Block style={roundedSizeStyle}>
        {imgSource ? (
          <Image
            contentFit="cover"
            height={size}
            source={imgSource}
            style={[roundedSizeStyle, style, !(uri || source) && styles.placeholder]}
            width={size}
          />
        ) : (
          <Block alignCenter justifyCenter flex={1} style={styles.placeholderIcon}>
            <SvgIcon color={palette.primary.pinkSalmon} name="profile" size={size * 1.3} />
          </Block>
        )}
      </Block>
      {confirmed && (
        <>
          <Block style={styles.avatarVerifiedBackground} />
          <Block style={styles.avatarVerifiedIcon}>
            <SvgIcon color={palette.common.lightGreen} name="done" size={20} />
          </Block>
        </>
      )}
    </>
  );
};

export default React.memo(Avatar);

const styles = StyleSheet.create({
  placeholder: {
    alignSelf: "center",
    backgroundColor: palette.primary.accent,
  },
  placeholderIcon: {
    backgroundColor: palette.primary.accent,
    overflow: "hidden",
  },
  avatarVerifiedBackground: {
    backgroundColor: palette.primary.accent,
    borderRadius: 50,
    bottom: 2,
    height: 15,
    left: 34,
    position: "absolute",
    width: 17,
  },
  avatarVerifiedIcon: {
    bottom: -3,
    left: 31,
    position: "absolute",
  },
});
